import { NavLink } from 'react-router-dom'
import React, { useState, useEffect, useRef } from 'react'
import { useAuth } from '../context/AuthContext'
import {
  Upload,
  FileText,
  Search,
  BookOpen,
  MessageSquare,
  BarChart2,
  AlertTriangle,
  CheckCircle,
  Eye,
  Zap,
  Plug,
  Puzzle,
  ShoppingCart,
  Layers,
  Users,
  Code,
  Key,
  Clock,
  CreditCard,
  Clipboard,
  Flag,
  Wrench,
  NotebookPen,
  Activity,
  PenTool,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  Sparkles,
  Target,
  CalendarClock,
  ShieldCheck,
  Brain,
  Bot,
  Library,
  FlaskConical,
} from 'lucide-react'

export default function LeftSidebar({ open = false, onClose = () => {} }: { open?: boolean; onClose?: () => void }) {
  const { user } = useAuth()
  const isAdmin = !!(user?.roles?.some((r: string) => r === 'ROLE_ADMIN' || r === 'ADMIN'))
  const showDevTools = Boolean((import.meta as any).env?.DEV || ((import.meta as any).env?.VITE_SHOW_DEV_TOOLS === 'true'))
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem('veridoc:sidebarCollapsed')
      return v ? JSON.parse(v) : false
    } catch (storageError) {
      console.warn('Unable to read sidebar state', storageError)
      return false
    }
  })
  const navRef = useRef<HTMLElement | null>(null)
  useEffect(() => {
    const cleanup = setupNavKeyboard(navRef.current)
    return () => { if (cleanup) cleanup() }
  }, [navRef])

  const link = 'flex items-center gap-3 px-3 py-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-sm'
  const active = ({ isActive }: { isActive: boolean }) => isActive ? `${link} bg-gray-100 dark:bg-gray-800 font-medium` : link

  // Sidebar classes: overlay on small screens, static on md+
  const base = `fixed inset-y-0 left-0 z-40 transform ${open ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-200 md:translate-x-0 md:static md:block`
  const widthClass = collapsed ? 'w-20' : 'w-64'

  return (
    <>
      {/* backdrop for small screens when open */}
      {open && <div className="fixed inset-0 z-30 bg-black/40 md:hidden" onClick={onClose} aria-hidden />}
      <aside className={`${base} ${widthClass} border-r border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shrink-0`} aria-hidden={!open && typeof window !== 'undefined' && window.innerWidth < 768}>
        <nav ref={navRef} aria-label="Main navigation" className="h-full overflow-y-auto py-4 px-1" role="navigation">
          <div className="flex items-center justify-between px-3 mb-3">
            <div className="text-sm font-semibold">Navigation</div>
            <div className="flex items-center gap-2">
              <button
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
                onClick={() => setCollapsed(c => {
                  const next = !c
                  try {
                    localStorage.setItem('veridoc:sidebarCollapsed', JSON.stringify(next))
                  } catch (persistError) {
                    console.warn('Unable to persist sidebar state', persistError)
                  }
                  return next
                })}
                aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                title={collapsed ? 'Expand' : 'Collapse'}
              >
                {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
              </button>
              <button className="p-1 rounded md:hidden" onClick={onClose} aria-label="Close sidebar" title="Close sidebar"><X size={14} /></button>
            </div>
          </div>

          <div className="mb-3 px-2 text-xs text-gray-500 uppercase font-semibold">Core</div>
          <div className="space-y-1 mb-4 px-1">
            <NavLink to="/upload" className={active}><Upload size={16} />{!collapsed && <span>Upload Documents</span>}</NavLink>
            <NavLink to="/documents" className={active}><FileText size={16} />{!collapsed && <span>Documents</span>}</NavLink>
            <NavLink to="/search" className={active}><Search size={16} />{!collapsed && <span>Search</span>}</NavLink>
            <NavLink to="/clause-explorer" className={active}><BookOpen size={16} />{!collapsed && <span>Clauses</span>}</NavLink>
            <NavLink to="/chat" className={active}><MessageSquare size={16} />{!collapsed && <span>Chat</span>}</NavLink>
          </div>

          <div className="mb-3 px-2 text-xs text-gray-500 uppercase font-semibold">Insights</div>
          <div className="space-y-1 mb-4 px-1">
            <NavLink to="/analytics" className={active}><BarChart2 size={16} />{!collapsed && <span>Analytics</span>}</NavLink>
            <NavLink to="/learning-center" className={active}><Sparkles size={16} />{!collapsed && <span>Learning Center</span>}</NavLink>
            <NavLink to="/collection-intelligence" className={active}><Target size={16} />{!collapsed && <span>Collections</span>}</NavLink>
            <NavLink to="/document-timeline" className={active}><CalendarClock size={16} />{!collapsed && <span>Timeline</span>}</NavLink>
            <NavLink to="/document-copilot" className={active}><Bot size={16} />{!collapsed && <span>Document Copilot</span>}</NavLink>
            <NavLink to="/contract-simulator" className={active}><FlaskConical size={16} />{!collapsed && <span>Contract Simulator</span>}</NavLink>
            <NavLink to="/what-changed-if" className={active}><Brain size={16} />{!collapsed && <span>What-Changed-If</span>}</NavLink>
            <NavLink to="/negotiation-checklist" className={active}><ShieldCheck size={16} />{!collapsed && <span>Negotiation</span>}</NavLink>
            <NavLink to="/clause-drift" className={active}><Activity size={16} />{!collapsed && <span>Clause Drift</span>}</NavLink>
            <NavLink to="/clause-library" className={active}><Library size={16} />{!collapsed && <span>Clause Library</span>}</NavLink>
            <NavLink to="/usage" className={active}><Clock size={16} />{!collapsed && <span>Usage</span>}</NavLink>
            <NavLink to="/anomalies" className={active}><AlertTriangle size={16} />{!collapsed && <span>Anomalies</span>}</NavLink>
            <NavLink to="/model-accuracy" className={active}><CheckCircle size={16} />{!collapsed && <span>Accuracy</span>}</NavLink>
            <NavLink to="/saved-views" className={active}><Eye size={16} />{!collapsed && <span>Views</span>}</NavLink>
          </div>

          <div className="mb-3 px-2 text-xs text-gray-500 uppercase font-semibold">Integrations</div>
          <div className="space-y-1 mb-4 px-1">
            <NavLink to="/integrations" className={active}><Zap size={16} />{!collapsed && <span>Integrations</span>}</NavLink>
            <NavLink to="/connectors" className={active}><Plug size={16} />{!collapsed && <span>Connectors</span>}</NavLink>
            <NavLink to="/plugins" className={active}><Puzzle size={16} />{!collapsed && <span>Plugins</span>}</NavLink>
            <NavLink to="/marketplace" className={active}><ShoppingCart size={16} />{!collapsed && <span>Marketplace</span>}</NavLink>
            <NavLink to="/webhooks" className={active}><Zap size={16} />{!collapsed && <span>Webhooks</span>}</NavLink>
          </div>

          <div className="mb-3 px-2 text-xs text-gray-500 uppercase font-semibold">Management</div>
          <div className="space-y-1 mb-4 px-1">
            <NavLink to="/workspaces" className={active}><Layers size={16} />{!collapsed && <span>Workspaces</span>}</NavLink>
            {isAdmin && <NavLink to="/users" className={active}><Users size={16} />{!collapsed && <span>Users</span>}</NavLink>}
            <NavLink to="/developers" className={active}><Code size={16} />{!collapsed && <span>Developers</span>}</NavLink>
            {isAdmin && <NavLink to="/playbooks" className={active}><NotebookPen size={16} />{!collapsed && <span>Playbooks</span>}</NavLink>}
            {isAdmin && <NavLink to="/clause-templates" className={active}><PenTool size={16} />{!collapsed && <span>Clause Templates</span>}</NavLink>}
            {isAdmin && <NavLink to="/apikeys" className={active}><Key size={16} />{!collapsed && <span>API Keys</span>}</NavLink>}
            {isAdmin && <NavLink to="/sessions" className={active}><Clock size={16} />{!collapsed && <span>Sessions</span>}</NavLink>}
          </div>

          <div className="mb-3 px-2 text-xs text-gray-500 uppercase font-semibold">Governance</div>
          <div className="space-y-1 mb-4 px-1">
            {isAdmin && <NavLink to="/billing" className={active}><CreditCard size={16} />{!collapsed && <span>Billing</span>}</NavLink>}
            {isAdmin && <NavLink to="/audit" className={active}><Clipboard size={16} />{!collapsed && <span>Audit</span>}</NavLink>}
            {isAdmin && <NavLink to="/flags" className={active}><Flag size={16} />{!collapsed && <span>Flags</span>}</NavLink>}
            <NavLink to="/diagnostics" className={active}><Wrench size={16} />{!collapsed && <span>Diagnostics</span>}</NavLink>
          </div>

          {showDevTools && (
            <>
              <div className="mb-3 px-2 text-xs text-gray-500 uppercase font-semibold">Dev Utilities</div>
              <div className="space-y-1 mb-4 px-1">
                <NavLink to="/demo-mode" className={active}><Menu size={16} />{!collapsed && <span>Demo Mode</span>}</NavLink>
              </div>
            </>
          )}
          {/* Keyboard navigation support: ArrowUp/Down, Home/End is wired on mount */}
        </nav>
      </aside>
    </>
  )
}

// Add keyboard handling after mount
function setupNavKeyboard(navEl: HTMLElement | null) {
  if (!navEl) return
  const handler = (e: KeyboardEvent) => {
    const keys = ['ArrowDown', 'ArrowUp', 'Home', 'End']
    if (!keys.includes(e.key)) return
    const links = Array.from(navEl.querySelectorAll<HTMLAnchorElement>('a[href]'))
    if (!links.length) return
    const active = document.activeElement as HTMLElement
    let idx = links.findIndex(l => l === active || l.contains(active))
    if (e.key === 'ArrowDown') { idx = (idx + 1) % links.length; links[idx].focus(); e.preventDefault() }
    if (e.key === 'ArrowUp') { idx = (idx - 1 + links.length) % links.length; links[idx].focus(); e.preventDefault() }
    if (e.key === 'Home') { links[0].focus(); e.preventDefault() }
    if (e.key === 'End') { links[links.length - 1].focus(); e.preventDefault() }
  }
  navEl.addEventListener('keydown', handler)
  return () => navEl.removeEventListener('keydown', handler)
}
